import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Checkbox, Stack, Typography, Skeleton, IconButton } from '@mui/material';

import Hls from 'hls.js';
import { debounce } from 'lodash';
import Icon from '../Icon';

import { VideoComponentProps } from './types';
import styles from './video.module.scss';
import { imageMap } from '.images';

const VideoComponent = ({
  data,
  error,
  autoplay = false,
  muted = true,
  playsInline,
  selectable,
  checked,
  onVideoSelect,
  onVideoClicked,
  onVideoDeselect,
  style,
  showFullVideo = false
}: VideoComponentProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [link, setLink] = useState('');

  useEffect(() => {
    if (data.media && data.media['_links'] && data.media['_links'].access.href) {
      setLink(data.media['_links'].access.href);
    } else {
      setLink('');
    }
  }, [data.media]);

  const playVideo = async () => {
    if (videoRef.current && !showFullVideo) {
      await videoRef.current.play().catch(e => {});
    }
  };

  const pauseVideo = () => {
    if (videoRef.current && !showFullVideo) {
      videoRef.current.pause();
    }
  };

  const handleMouseOver = () => {
    setIsFocused(true);
  };

  const handleMouseOut = () => {
    debouncedMouseOver.cancel();
    setIsFocused(false);
    pauseVideo();
  };

  const handleVideoEnded = () => {
    if (isFocused) {
      playVideo();
    }
  };

  useEffect(() => {
    if (isFocused) {
      playVideo();
    }
  }, [isFocused]);

  const handleVideoChecked = (item: any, checked: boolean) => {
    if (onVideoSelect) {
      onVideoSelect(item, checked);
    }
  };

  const handleVideoClicked = (data: any) => {
    if (onVideoClicked) {
      onVideoClicked(data);
    }
  };

  const handleVideoDeselect = (id: string) => {
    if (onVideoDeselect) {
      onVideoDeselect(id);
    }
  };

  useEffect(() => {
    let hls: Hls;
    if (link) {
      if (Hls.isSupported()) {
        const video = videoRef.current!;
        hls = new Hls();
        hls.on(Hls.Events.MEDIA_ATTACHED, function () {});
        hls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {});
        hls.loadSource(link);
        // bind them together
        hls.attachMedia(video);
      }
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [link]);

  const debouncedMouseOver = useCallback(debounce(handleMouseOver, 500), [500]);

  useEffect(() => {
    return () => {
      debouncedMouseOver.cancel();
    };
  }, [debouncedMouseOver]);

  return (
    <Box
      position="relative"
      height="100%"
      onMouseEnter={debouncedMouseOver}
      onMouseLeave={handleMouseOut}
      style={style}
      onClick={() => handleVideoClicked(data)}
    >
      <Box
        position="absolute"
        p={1}
        sx={{
          background: 'rgba(0, 0, 0, 0.3)',
          // height: '100%',
          borderRadius: '6px',
          width: '100%',
          transition: 'visibility 0.3s ease-in-out',
          visibility: isFocused || checked ? 'visible' : 'hidden'
        }}
      >
        <Stack direction="row" justifyContent="end" alignItems="flex-start">
          {selectable && (
            <Checkbox
              checked={checked}
              onChange={(_, checked) => handleVideoChecked(data, checked)}
              sx={{ p: 0, zIndex: 1 }}
              size="small"
            />
          )}
        </Stack>
      </Box>
      <Box
        zIndex={1}
        sx={{
          width: '100%',
          position: 'absolute',
          bottom: 0,
          padding: 1,
          background: 'linear-gradient(0deg,rgba(0,0,0,.5),transparent)',
          transition: 'visibility 0.3s ease-in-out',
          visibility: isFocused || checked ? 'visible' : 'hidden'
        }}
      >
        <Typography color="white" fontSize={12} fontWeight={500}>
          {data.title}
        </Typography>
      </Box>
      {data?.src && !error ? (
        <Box position="relative" sx={{ background: '#E1EBF2', height: '100%', minHeight: '350px' }}>
          {showFullVideo !== true ? (
            <video
              ref={videoRef}
              poster={data?.asset?.thumbnail}
              className={styles.video}
              src={isFocused ? data.src : null}
              muted={muted}
              autoPlay={isFocused ? autoplay : false}
              playsInline={playsInline}
              onEnded={handleVideoEnded}
              style={{ background: '#E1EBF2', height: '100%', minHeight: '350px', objectFit: 'cover', width: '100%' }}
            />
          ) : null}

          {/* {
            showFullVideo !== true ? isFocused ? <video
              ref={videoRef}
              poster={data.thumbnail}
              className={styles.video}
              src={data.src}
              muted={muted}
              autoPlay={autoplay}
              playsInline={playsInline}
              onEnded={handleVideoEnded}
              style={{ background: '#E1EBF2', height: '100%', minHeight: '350px', objectFit: 'cover' }}
            /> : <img src={data.asset.thumbnail} style={{ background: '#E1EBF2', height: '100%', minHeight: '350px', objectFit: 'cover', borderRadius:'6px' }} /> : null
          } */}

          {showFullVideo == true && (
            <video
              ref={videoRef}
              poster={data.thumbnail}
              className={styles.videoFull}
              // src={data.src}
              muted={muted}
              autoPlay={autoplay}
              playsInline={playsInline}
              controls={true}
              onEnded={handleVideoEnded}
              // onClick={() => handleVideoClicked(data)}
              style={{ background: '#E1EBF2', height: '100%', minHeight: '350px', objectFit: 'cover' }}
            >
              <source src={link} type="video/mp4"></source>
              <source src={link} type="video/mov"></source>
              <source src={link} type="video/webm"></source>
              <source src={link} type="application/x-mpegURL"></source>
            </video>
          )}
          {onVideoDeselect && (
            <IconButton
              sx={{ position: 'absolute', right: 0, top: 0, borderRadius: '4px', padding: '0' }}
              disableTouchRipple
              disableRipple
              onClick={() => handleVideoDeselect(data?.id)}
            >
              <Icon size={'24'} name="close" />
            </IconButton>
          )}
        </Box>
      ) : !data?.src && error ? (
        <img src={imageMap['video-error']()} height="100%" style={{ objectFit: 'cover', borderRadius: 6 }} />
      ) : (
        // <Skeleton variant="rounded" width="100%" height={300} />
        <Box
          sx={{
            height: '350px',
            width: '100%',
            background: '#E1EBF2',
            borderRadius: '8px',
            position: 'relative'
          }}
        >
          <Skeleton
            variant={'rectangular'}
            width={'100%'}
            height={'100%'}
            sx={{ position: 'absolute', background: '#E1EBF2', borderRadius: '8px' }}
            animation={'wave'}
          />
          <Stack direction={'column'} sx={{ height: '100%', display: 'flex', justifyContent: 'end', padding: 1 }}>
            <Skeleton width={'204px'} height={'14px'} />
            <Skeleton width={'148px'} height={'14px'} />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default VideoComponent;
